import type { TboHotelSyncRequestInput } from '@utazzo/shared';

const STORAGE_KEY = 'utazzo:tbo-hotel-sync-checkpoint';

/** Saved cursor + totals so a stopped or failed sync can resume. */
export type TboHotelSyncCheckpoint = {
  countryCode: string;
  tboCityId?: string;
  cityIndex: number;
  offset: number;
  upserted: number;
  errors: number;
  processedTotal: number;
  lastCityId: string | null;
  totalInCity: number | null;
  savedAt: string;
};

export const buildTboHotelSyncCheckpoint = (
  input: Pick<TboHotelSyncRequestInput, 'countryCode' | 'tboCityId'>,
  cursor: { cityIndex: number; offset: number },
  totals: { upserted: number; errors: number; processedTotal: number },
  meta: { lastCityId: string | null; totalInCity: number | null },
): TboHotelSyncCheckpoint => ({
  countryCode: input.countryCode.toUpperCase(),
  ...(input.tboCityId ? { tboCityId: input.tboCityId } : {}),
  cityIndex: cursor.cityIndex,
  offset: cursor.offset,
  upserted: totals.upserted,
  errors: totals.errors,
  processedTotal: totals.processedTotal,
  lastCityId: meta.lastCityId,
  totalInCity: meta.totalInCity,
  savedAt: new Date().toISOString(),
});

export const loadTboHotelSyncCheckpoint = (): TboHotelSyncCheckpoint | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isCheckpoint(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const saveTboHotelSyncCheckpoint = (checkpoint: TboHotelSyncCheckpoint): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(checkpoint));
};

export const clearTboHotelSyncCheckpoint = (): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORAGE_KEY);
};

export const formatTboHotelSyncCheckpoint = (cp: TboHotelSyncCheckpoint): string => {
  const scope = cp.tboCityId
    ? `${cp.countryCode} · city ${cp.tboCityId}`
    : `${cp.countryCode} · all cities`;
  const progress =
    cp.totalInCity !== null && cp.totalInCity !== undefined && cp.totalInCity > 0
      ? `${Math.min(cp.offset, cp.totalInCity)}/${cp.totalInCity} hotels in current city`
      : `${cp.processedTotal} hotels processed`;
  return `${scope}- ${progress} (${cp.upserted} upserted)`;
};

const isCheckpoint = (value: unknown): value is TboHotelSyncCheckpoint => {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.countryCode === 'string' &&
    typeof v.cityIndex === 'number' &&
    typeof v.offset === 'number' &&
    typeof v.upserted === 'number' &&
    typeof v.errors === 'number' &&
    typeof v.processedTotal === 'number' &&
    typeof v.savedAt === 'string'
  );
};
